/**
 * User Detail By ID Page
 * 
 * Displays user detail fetched by ID from URL params.
 */

import * as React from "react";
import { useRoute } from "wouter";
import { UserDetailPage } from "./UserDetailPage";
import { userService } from "@/services/userService";
import type { User } from "@/services/authService";
import { Loader2 } from "lucide-react";

export function UserDetailByIdPage() {
  const [, params] = useRoute<{ id: string }>("/user/detail/:id");
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const userId = params?.id;

  const refetchUser = React.useCallback(async () => {
    if (!userId) return;
    try {
      const userData = await userService.fetchUserDetailById(userId);
      setUser(userData);
    } catch (err) {
      console.error("Error fetching user detail:", err);
    }
  }, [userId]);

  React.useEffect(() => {
    if (!userId) {
      setError("User ID tidak ditemukan");
      setLoading(false);
      return;
    }

    const fetchUser = async () => {
      setLoading(true);
      setError(null);

      try {
        const userData = await userService.fetchUserDetailById(userId);
        setUser(userData);
      } catch (err) {
        console.error("Error fetching user detail:", err);
        setError(err instanceof Error ? err.message : "Gagal memuat data pengguna");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Memuat data pengguna...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex-1 space-y-4 p-6">
        <div className="text-center text-red-600 dark:text-red-400">
          {error || "Pengguna tidak ditemukan"}
        </div>
      </div>
    );
  }

  // Use the existing UserDetailPage component but pass the fetched user
  // We'll need to modify UserDetailPage to accept a user prop
  return <UserDetailPage user={user} onViewedUserRefresh={refetchUser} />;
}

